import axios from 'axios';
import type {CacheRequestConfig} from 'axios-cache-interceptor';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {PdfExportError} from 'features/pdf-export';

import type {GridPdfExportRequest} from '../types/GridPdfExportRequest';
import {AXIOS_BASE_CONFIG} from './axiosConfig';
import {gridDataProtocol} from './gridDataProtocol';

const errorSchema = z.object({errors: z.array(z.object({code: z.string()}))});

/** Fresh read through the configured client. Never route an export failure into the grid error state. */
export function getPdfExportData(request: GridPdfExportRequest, signal: AbortSignal) {
  signal.throwIfAborted();
  const config: CacheRequestConfig<unknown, {columns: readonly string[]}> = {
    ...AXIOS_BASE_CONFIG,
    signal,
    cache: false,
    // A cache-disabled export must not clear a matching grid request's cache-wait entry.
    id: `pdf-export:${crypto.randomUUID()}`,
  };
  return axios
    .post<unknown>(gridDataProtocol.pageUrl(request), {columns: request.requiredColumns}, config)
    .then((response) => {
      signal.throwIfAborted();
      const parsed = gridDataProtocol.responseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new PdfExportError('module.reportDocument.labels.dataUnavailable');
      }
      return gridDataProtocol.normalizeRows(parsed.data.rows);
    })
    .catch((error: unknown) => {
      if (!axios.isAxiosError(error)) {
        throw error;
      }
      const parsed = errorSchema.safeParse(error.response?.data);
      return match(error.response?.status)
        .with(401, 403, () => {
          throw new PdfExportError('general.notifications.noPermission');
        })
        .with(404, 410, () => {
          throw new PdfExportError(
            'module.reportDocument.labels.dataUnavailable',
            false,
            'entity.integration.labels.pleaseRefresh'
          );
        })
        .when(
          () => parsed.success && parsed.data.errors.some((item) => item.code === 'PKG-DG-0010'),
          () => {
            throw new PdfExportError(
              'module.reportDocument.labels.dataUnavailable',
              false,
              'entity.integration.labels.pleaseRefresh'
            );
          }
        )
        .otherwise(() => {
          throw error;
        });
    });
}
